import { useGet, usePost } from '@src/api/methods';
import {
  Button,
  Form,
  Input,
  Switch,
  Table,
  Tag,
  message,
  Card,
  Space,
  Row,
  Col,
  Typography,
  Spin
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useWatch } from 'antd/es/form/Form';

const { TextArea } = Input;
const { Text } = Typography;

export default function ApplicationStatus() {
  const {
    data: ocrData,
    isLoading: ocrGetLoading,
    refetch: ocrRefetch
  } = useGet('/application/status');
  const { mutate: createOcrRequest, isLoading: ocrLoading } = usePost(
    '/application/status'
  );

  const {
    data: extensionData,
    isLoading: extensionGetLoading,
    refetch: extensionRefetch
  } = useGet('/extension-statuses');
  const { mutate: createExtensionRequest, isLoading: extensionPostLoading } =
    usePost('/admin/extension-statuses');

  const [ocrForm] = Form.useForm();
  const [extensionForm] = Form.useForm();

  const ocrStatus = useWatch('status', ocrForm);
  const extensionStatus = useWatch('status', extensionForm);

  const handleOcrSubmit = (values) => {
    const reversedStatus = values.status ? false : true;
    values.status = reversedStatus ? 'ACTIVE' : 'INACTIVE';

    const body = {
      status: values.status,
      title: values.title || '',
      message: values.message || ''
    };

    createOcrRequest(body, {
      onSuccess: () => {
        ocrRefetch();
        ocrForm.resetFields();
        message.success('Başvuru durumu başarıyla güncellendi.');
      },
      onError: () => {
        message.error('Başvuru durumu güncellenirken bir hata oluştu!');
      }
    });
  };

  const handleExtensionSubmit = (values) => {
    const reversedStatus = values.status ? false : true;

    const body = {
      isEnabled: reversedStatus,
      maintenanceMessage: values.message || ''
    };

    createExtensionRequest(body, {
      onSuccess: () => {
        extensionRefetch();
        extensionForm.resetFields();
        message.success('Extension durumu başarıyla güncellendi.');
      },
      onError: () => {
        message.error('Extension durumu güncellenirken bir hata oluştu!');
      }
    });
  };

  const ocrColumns = [
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (text) => {
        const isActive = text === 'ACTIVE';
        return (
          <Tag
            icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isActive ? 'success' : 'warning'}
          >
            {isActive
              ? 'OCR Yöntemi Açık'
              : 'OCR Yöntemi Kapalı ve Mesaj Gösterilecek'}
          </Tag>
        );
      },
      width: '20%'
    },
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      width: '25%'
    },
    {
      title: 'Mesaj',
      dataIndex: 'message',
      key: 'message',
      width: '55%'
    }
  ];

  const extensionColumns = [
    {
      title: 'Durum',
      dataIndex: 'extensionStatuses',
      key: 'extensionStatuses',
      render: (text) => {
        const status = text.isEnabled;
        const isActive = status ? true : false;
        return (
          <Tag
            icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isActive ? 'success' : 'warning'}
          >
            {isActive
              ? 'Extension Yöntemi Açık'
              : 'Extension Yöntemi Kapalı ve Mesaj Gösterilecek'}
          </Tag>
        );
      },
      width: '20%'
    },
    {
      title: 'Bakım Mesajı',
      dataIndex: ['extensionStatuses', 'maintenanceMessage'],
      key: 'extensionStatuses.maintenanceMessage',
      width: '80%'
    }
  ];

  if (ocrGetLoading || extensionGetLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Durum bilgileri yükleniyor...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Space className="kemi_form_highlighted_header w-full mb-0">
        <InfoCircleOutlined />
        <span>Mevcut Durum</span>
      </Space>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Current Status Section */}
        <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <h3 className="kemi_form_highlighted_secondary_header">
                OCR Durumu
              </h3>
              <Table
                columns={ocrColumns}
                dataSource={ocrData ? [ocrData] : []}
                pagination={false}
                size="middle"
                style={{ marginBottom: '24px' }}
                rowKey="id"
                key="ocrData"
              />
            </div>

            <div>
              <h3 className="kemi_form_highlighted_secondary_header">
                Extension Durumu
              </h3>
              <Table
                columns={extensionColumns}
                dataSource={extensionData ? [extensionData] : []}
                pagination={false}
                size="middle"
                rowKey="id"
                key="extensionData"
              />
            </div>
          </Space>
        </Card>

        {/* Update Forms Section */}
        <Row gutter={[24, 24]}>
          {/* OCR Update Form */}
          <Col xs={24} lg={12}>
            <Card
              style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            >
              <h3 className="kemi_form_highlighted_secondary_header">
                <EditOutlined /> OCR Durumu Güncelle
              </h3>
              <Form
                onFinish={handleOcrSubmit}
                form={ocrForm}
                layout="vertical"
                requiredMark={false}
              >
                <Space
                  direction="vertical"
                  size="middle"
                  className="flex flex-col h-full w-full"
                >
                  <Row gutter={16}>
                    <Col span={16}>
                      <Form.Item label="Başlık" name="title">
                        <Input
                          placeholder="Durum başlığını giriniz"
                          disabled={!ocrStatus}
                          style={{
                            opacity: !ocrStatus ? 0.5 : 1
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Durum"
                        name="status"
                        initialValue={false}
                        valuePropName="checked"
                        tooltip="Engelleme açık: Yöntem kapalı ve mesaj gösterilecek | Engelleme kapalı: Yöntem açık"
                      >
                        <Switch
                          checkedChildren="Engelleme açık"
                          unCheckedChildren="Engelleme kapalı"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Mesaj" name="message">
                    <TextArea
                      rows={4}
                      placeholder="Durum mesajını giriniz"
                      showCount
                      maxLength={500}
                      disabled={!ocrStatus}
                    />
                  </Form.Item>

                  <div style={{ textAlign: 'right' }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={ocrLoading}
                      icon={<SaveOutlined />}
                      size="large"
                    >
                      Kaydet
                    </Button>
                  </div>
                </Space>
              </Form>
            </Card>
          </Col>

          {/* Extension Update Form */}
          <Col xs={24} lg={12}>
            <Card
              style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            >
              <h3 className="kemi_form_highlighted_secondary_header">
                <EditOutlined /> Extension Durumu Güncelle
              </h3>
              <Form
                onFinish={handleExtensionSubmit}
                form={extensionForm}
                layout="vertical"
                requiredMark={false}
              >
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: '100%' }}
                >
                  <Form.Item
                    label="Durum"
                    name="status"
                    valuePropName="checked"
                    initialValue={false}
                    tooltip="Engelleme açık: Yöntem kapalı ve mesaj gösterilecek | Engelleme kapalı: Yöntem açık"
                  >
                    <Switch
                      checkedChildren="Engelleme Açık"
                      unCheckedChildren="Engelleme Kapalı"
                    />
                  </Form.Item>

                  <Form.Item label="Bakım Mesajı" name="message">
                    <TextArea
                      disabled={!extensionStatus}
                      rows={6}
                      placeholder="Bakım mesajını giriniz"
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>

                  <div style={{ textAlign: 'right' }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={extensionPostLoading}
                      icon={<SaveOutlined />}
                      size="large"
                    >
                      Kaydet
                    </Button>
                  </div>
                </Space>
              </Form>
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
}
